import { useContext } from 'react';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCourseSummary,
  fetchLastCompletedCourseUnit,
  fetchLearnerHomeInit,
} from '@blend-ed/blendxplatform/api';

import { useCalendarEvents, useFetchAllFlashcards, useFetchBanners } from '../../gql';
import { selectDashboardBanner } from './components/DashboardBanner';
import messages from './messages';

/**
 * The three dashboard states. State is a derived property — it is recomputed on
 * every render from live signals, never stored. See the Learner Dashboard
 * mental model for the full rendering contract.
 *
 *  DISCOVERY      (A) — zero enrollments.
 *  PRE_ENGAGEMENT (B) — enrolled, but no course has a started unit.
 *  ACTIVE         (C) — at least one in-progress course.
 */
export const DASHBOARD_STATE = {
  DISCOVERY: 'discovery',
  PRE_ENGAGEMENT: 'preEngagement',
  ACTIVE: 'active',
};

// Resume-hero badge reflects the last unit's xblock type. Map the known
// learner-facing types to translatable labels; fall back to the re-cased raw
// string for any type without a dedicated message.
const ACTIVITY_BADGE_KEYS = {
  video: 'activityVideo',
  problem: 'activityProblem',
  html: 'activityReading',
  discussion: 'activityDiscussion',
  openassessment: 'activityAssessment',
};

const getActivityBadge = (activity, intl) => {
  if (!activity) {
    return intl.formatMessage(messages.courseBadge);
  }
  const key = ACTIVITY_BADGE_KEYS[activity.toLowerCase()];
  if (key) {
    return intl.formatMessage(messages[key]);
  }
  return activity.charAt(0).toUpperCase() + activity.slice(1).toLowerCase();
};

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60';

const startsInFuture = (startDate) => {
  if (!startDate) { return false; }
  const d = new Date(startDate);
  return !Number.isNaN(d.getTime()) && d > new Date();
};

const isUsablePercent = (value) => typeof value === 'number' && !Number.isNaN(value);

/**
 * Resolves a course's completion percentage, preferring the value carried on
 * the learner-init payload and falling back to the course-summary endpoint.
 */
const resolveProgressPercent = (initPercent, summaryPercent) => {
  if (isUsablePercent(initPercent)) { return Math.round(initPercent); }
  if (isUsablePercent(summaryPercent)) { return Math.round(summaryPercent); }
  return 0;
};

/**
 * Flattens the course-summary endpoint (several possible shapes) into a
 * { courseId: completePercentage } map used as a fallback progress source.
 */
const buildProgressByCourseId = (summary) => {
  if (!summary) { return {}; }

  const candidates = [];
  if (Array.isArray(summary)) { candidates.push(...summary); }
  if (Array.isArray(summary?.courses)) { candidates.push(...summary.courses); }
  if (Array.isArray(summary?.course_summaries)) { candidates.push(...summary.course_summaries); }

  if (candidates.length === 0 && typeof summary === 'object') {
    const values = Object.values(summary);
    if (values.every((v) => typeof v === 'object' && v !== null)) {
      candidates.push(...values);
    }
  }

  return candidates.reduce((acc, item) => {
    const courseId = item?.course_id || item?.courseId || item?.id || item?.course_key;
    if (!courseId) { return acc; }

    const percent = item?.complete_percentage
      ?? item?.completion_percentage
      ?? item?.percent_complete
      ?? item?.progress_percent
      ?? item?.progressPercent;

    if (typeof percent === 'number' && !Number.isNaN(percent)) {
      acc[courseId] = percent;
    }
    return acc;
  }, {});
};

/**
 * Derives the dashboard state and the view-model for whichever components that
 * state renders. All conditional rendering decisions live here — the page
 * component only switches on `state`.
 */
const useDashboardState = () => {
  const intl = useIntl();
  const { authenticatedUser } = useContext(AppContext);

  const name = authenticatedUser?.name || authenticatedUser?.username || '';
  const userId = authenticatedUser?.userId || authenticatedUser?.id || authenticatedUser?.user_id || null;

  const { data: learnerHomeInit, isLoading: initLoading } = useQuery({
    queryKey: ['learnerHomeInit'],
    queryFn: fetchLearnerHomeInit,
  });

  const { data: lastCompletedCourseUnit } = useQuery({
    queryKey: ['lastCompletedCourseUnit'],
    queryFn: fetchLastCompletedCourseUnit,
  });

  const { data: courseSummary } = useQuery({
    queryKey: ['courseSummary', userId],
    queryFn: () => fetchCourseSummary(userId),
    enabled: !!userId,
  });

  const { flashcards } = useFetchAllFlashcards();
  const { events } = useCalendarEvents(true);
  const { banners } = useFetchBanners();

  const progressByCourseId = buildProgressByCourseId(courseSummary);

  // --- Signal 1 & 2: enrollments and per-course progress -------------------
  const enrollments = (learnerHomeInit?.courses || [])
    .map((course) => {
      const courseId = course?.courseRun?.courseId;
      const completionFromInit = course?.completionData?.complete_percentage
        ?? (courseId ? learnerHomeInit?.completionData?.[courseId]?.complete_percentage : null);
      const completionFromSummary = courseId ? progressByCourseId[courseId] : null;

      const progressPercent = resolveProgressPercent(completionFromInit, completionFromSummary);

      return {
        id: courseId || course?.course?.courseName,
        title: course?.course?.courseName || intl.formatMessage(messages.fallbackCourseTitle),
        progressPercent,
        imageUrl: course?.course?.bannerImgSrc
          ? `${getConfig().LMS_BASE_URL}${course.course.bannerImgSrc}`
          : FALLBACK_IMAGE,
        homeUrl: course?.courseRun?.homeUrl || null,
        future: startsInFuture(course?.courseRun?.startDate),
      };
    })
    .filter((c) => !!c.id);

  const enrollmentCount = enrollments.length;
  const startedCount = enrollments.filter((c) => c.progressPercent > 0).length;

  // --- Derive the state ----------------------------------------------------
  let state = DASHBOARD_STATE.ACTIVE;
  if (enrollmentCount === 0) {
    state = DASHBOARD_STATE.DISCOVERY;
  } else if (startedCount === 0) {
    state = DASHBOARD_STATE.PRE_ENGAGEMENT;
  }

  // --- Ongoing courses (State C): started, not finished, available now -----
  const ongoingItems = enrollments
    .filter((c) => !c.future && c.progressPercent > 0 && c.progressPercent < 100)
    .slice(0, 8)
    .map((c) => ({
      id: c.id,
      title: c.title,
      progressPercent: c.progressPercent,
      badgeText: intl.formatMessage(messages.courseBadge),
      imageUrl: c.imageUrl,
      homeUrl: c.homeUrl,
    }));

  // --- Start Learning hero (State B): most recently enrolled, not started --
  const startSource = enrollments.find((c) => !c.future && c.progressPercent === 0)
    || enrollments[0]
    || null;
  const startCourse = startSource ? {
    title: startSource.title,
    imageUrl: startSource.imageUrl,
    homeUrl: startSource.homeUrl,
  } : null;

  // --- Resume hero (State C): most recently accessed in-progress course ----
  let resumeCourse = null;
  if (lastCompletedCourseUnit?.resume_url) {
    resumeCourse = {
      title: lastCompletedCourseUnit.course_title,
      progressPercent: Math.round(lastCompletedCourseUnit?.completion_data?.complete_percentage ?? 0),
      badgeText: getActivityBadge(lastCompletedCourseUnit.activity_type, intl),
      imageUrl: lastCompletedCourseUnit.course_image || FALLBACK_IMAGE,
      resumeUrl: lastCompletedCourseUnit.resume_url,
    };
  } else if (ongoingItems.length > 0) {
    const [first] = ongoingItems;
    resumeCourse = {
      title: first.title,
      progressPercent: first.progressPercent,
      badgeText: first.badgeText,
      imageUrl: first.imageUrl,
      resumeUrl: first.homeUrl,
    };
  }

  return {
    status: initLoading ? 'loading' : 'ready',
    state,
    name,
    enrollmentCount,
    startedCount,
    ongoingItems,
    startCourse,
    resumeCourse,
    // Schedule widget in State B renders only when a session is upcoming.
    hasUpcomingSession: events.length > 0,
    flashcardCount: flashcards.length,
    banner: selectDashboardBanner(banners),
  };
};

export default useDashboardState;
